// The add-in handoff. The Excel task pane opens this portal inside an Office dialog with
// `?client=addin&addin_origin=<origin>`; once the user is signed in (signup, login, or the Microsoft flow) the
// portal sends the tokens to the add-in's dialog page in the URL fragment — the contract of
// peerception-agent-v2/src/taskpane/authdialog/flow.ts, the same one sculpt-api's Microsoft flow uses. The
// dialog page posts them to the pane and the pane closes the dialog: the user never signs in twice.
//
// Only origins in VITE_ADDIN_ORIGINS receive tokens (keep it in sync with the API's SCULPT_OAUTH_ORIGINS).
// The handoff survives the navigation between /signup and /login inside the dialog through sessionStorage.
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Tokens } from '../types';

const KEY = 'sculpt.account.handoff';
const DEFAULT_ORIGINS = 'https://localhost:3003';

export interface Handoff {
  origin: string;
}

export function allowedOrigins(): string[] {
  return (import.meta.env.VITE_ADDIN_ORIGINS ?? DEFAULT_ORIGINS)
    .split(',')
    .map((s) => s.trim().replace(/\/$/, ''))
    .filter(Boolean);
}

/** The handoff named by a query string, or null when absent or when the origin is not allowed. */
export function handoffFromSearch(search: string): Handoff | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('client') !== 'addin') return null;
  const origin = (params.get('addin_origin') ?? '').trim().replace(/\/$/, '');
  return origin && allowedOrigins().includes(origin) ? { origin } : null;
}

export function activeHandoff(): Handoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Handoff>;
    return typeof parsed.origin === 'string' && allowedOrigins().includes(parsed.origin) ? { origin: parsed.origin } : null;
  } catch {
    return null;
  }
}

export function rememberHandoff(search: string): Handoff | null {
  const handoff = handoffFromSearch(search);
  if (handoff) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(handoff));
    } catch {
      /* the handoff still works for this page */
    }
  }
  return handoff;
}

export function clearHandoff(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Where the tokens go: the add-in's dialog page, with the fragment its `parseAuthHash` reads. */
export function handoffUrl(handoff: Handoff, tokens: Tokens): string {
  const params = new URLSearchParams({ access_token: tokens.access_token, token_type: tokens.token_type || 'bearer' });
  if (tokens.refresh_token) params.set('refresh_token', tokens.refresh_token);
  return `${handoff.origin}/auth-dialog.html#${params.toString()}`;
}

export function completeHandoff(handoff: Handoff, tokens: Tokens, go: (url: string) => void = (url) => window.location.replace(url)): void {
  clearHandoff();
  go(handoffUrl(handoff, tokens));
}

/** The handoff of the current page: named by its query string, or remembered from an earlier page of the dialog. */
export function useHandoff(): Handoff | null {
  const { search } = useLocation();
  return useMemo(() => rememberHandoff(search) ?? activeHandoff(), [search]);
}
